// @ts-nocheck
import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Download,
  Search,
  ArrowLeft,
  Loader2,
  Sheet
} from 'lucide-react';
import { useRegistrations, Registration } from '@/modules/admin/hooks/useRegistrations';
import { RegistrationCard } from '@/components/admin/RegistrationCard';
import { RegistrationDetailsModal } from '@/components/admin/RegistrationDetailsModal';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDateTime } from '@/utils/formatters';
import { getNormalizedRegistrationPricing, type MinimalRegistrationForPricing } from '@/utils/orderPricing';
import { SignedWaiversPanel } from '@/components/admin/SignedWaiversPanel';
import TeamDataGrids from '@/components/admin/teams/TeamDataGrids';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

export default function EventRegistrationsAdmin() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);

  const [syncing, setSyncing] = useState(false);

  const { registrations, loading, stats } = useRegistrations(eventId!);

  // Fetch event details with Google Sheets config
  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, start_date, google_sheets_webhook_url, google_sheets_tab_name, google_sheets_column_mapping')
        .eq('id', eventId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!eventId
  });

  const normalizedById = useMemo(() => {
    const map: Record<string, { totalCents: number; regAmountCents: number }> = {};

    const getExtrasCents = (r: Registration) =>
      typeof (r as any).extras_amount === 'number'
        ? Math.round(((r as any).extras_amount ?? 0) * 100)
        : (r as any).pricing_breakdown?.extras_total ?? 0;

    const getSpecialCents = (r: Registration) =>
      typeof (r as any).special_request_amount === 'number'
        ? Math.round(((r as any).special_request_amount ?? 0) * 100)
        : (r as any).pricing_breakdown?.special_requests_fee ?? 0;

    const getTicketsCents = (r: Registration) =>
      typeof (r as any).tickets_amount === 'number'
        ? Math.round(((r as any).tickets_amount ?? 0) * 100)
        : (r as any).pricing_breakdown?.tickets_total ?? 0;

    // Default values for all registrations
    for (const r of registrations) {
      const regAmountCents =
        typeof (r as any).registration_amount === 'number'
          ? Math.round(((r as any).registration_amount ?? 0) * 100)
          : (r as any).pricing_breakdown?.base_price ?? 0;

      const totalCents =
        (r as any).pricing_breakdown?.total ??
        regAmountCents + getExtrasCents(r) + getSpecialCents(r) + getTicketsCents(r);

      map[r.id] = { totalCents, regAmountCents };
    }

    // Override grouped registrations with normalized per-team amounts
    const byOrderGroup: Record<string, Registration[]> = {};
    for (const r of registrations) {
      const key = (r as any).order_group_id;
      if (!key) continue;
      if (!byOrderGroup[key]) byOrderGroup[key] = [];
      byOrderGroup[key].push(r);
    }

    for (const group of Object.values(byOrderGroup)) {
      const pricingGroup: MinimalRegistrationForPricing[] = group.map((r: any) => ({
        id: r.id,
        order_group_id: r.order_group_id,
        registration_amount: r.registration_amount ?? null,
        pricing_breakdown: r.pricing_breakdown as any,
      }));

      for (const r of group) {
        const { regAmountCents } = getNormalizedRegistrationPricing(
          {
            id: r.id,
            order_group_id: (r as any).order_group_id,
            registration_amount: (r as any).registration_amount ?? null,
            pricing_breakdown: (r as any).pricing_breakdown as any,
          },
          pricingGroup
        );

        const totalCents =
          regAmountCents + getExtrasCents(r) + getSpecialCents(r) + getTicketsCents(r);

        map[r.id] = { totalCents, regAmountCents };
      }
    }

    return map;
  }, [registrations]);

  // Filter registrations
  const filteredRegistrations = useMemo(() => {
    let filtered = registrations;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.registrant_data.contact_name?.toLowerCase().includes(query) ||
        r.registrant_data.contact_email?.toLowerCase().includes(query) ||
        r.registrant_data.contact_phone?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [registrations, statusFilter, searchQuery]);

  const exportToCSV = () => {
    if (registrations.length === 0) {
      toast.error('No registrations to export');
      return;
    }

    const headers = [
      'Name',
      'Email',
      'Phone',
      'Type',
      'Tier/Division',
      'Status',
      'Payment Status',
      'Entry Fee',
      'Extras Total',
      'Tickets Total',
      'Schedule Request',
      'Schedule Request Fee',
      'Grand Total',
      'Date'
    ];

    const rows = registrations.map(r => {
      // Use the normalized entry fee amount (excludes extras, special requests, tickets)
      const { regAmountCents } = normalizedById[r.id] || { regAmountCents: 0 };
      const breakdown = r.pricing_breakdown as Record<string, any> || {};
      
      // Calculate extras total from pricing breakdown
      let extrasTotal = 0;
      if (breakdown.extras && Array.isArray(breakdown.extras)) {
        extrasTotal = breakdown.extras.reduce((sum: number, ext: any) => {
          const price = ext.price || ext.priceCents || 0;
          const qty = ext.quantity || 1;
          return sum + (price * qty);
        }, 0);
      }
      
      // Calculate tickets total from pricing breakdown
      let ticketsTotal = 0;
      if (breakdown.tickets && Array.isArray(breakdown.tickets)) {
        ticketsTotal = breakdown.tickets.reduce((sum: number, ticket: any) => {
          const price = ticket.price || ticket.priceCents || 0;
          const qty = ticket.quantity || 1;
          return sum + (price * qty);
        }, 0);
      }
      
      // Get schedule request fee - check all possible field names
      const scheduleRequestFee = breakdown.special_request_fee || breakdown.specialRequestFee || breakdown.schedule_request_fee || 0;
      
      // Get schedule request details text from custom_responses
      const customResponses = (r.registrant_data as any)?.custom_responses || {};
      let scheduleRequestDetails = '';
      // Look for form_X_special_request_details in custom_responses
      for (const key of Object.keys(customResponses)) {
        if (key.includes('special_request_details') && customResponses[key]) {
          scheduleRequestDetails = customResponses[key];
          break;
        }
      }
      // Also check nested special_requests object
      if (!scheduleRequestDetails && (r.registrant_data as any)?.special_requests?.details) {
        scheduleRequestDetails = (r.registrant_data as any).special_requests.details;
      }
      
      // Calculate grand total (entry fee + extras + tickets + schedule request)
      const grandTotal = regAmountCents + extrasTotal + ticketsTotal + scheduleRequestFee;
      
      return [
        r.registrant_data.contact_name || '',
        r.registrant_data.contact_email || '',
        r.registrant_data.contact_phone || '',
        r.registration_type,
        r.registrant_data.division || r.registrant_data.pricing_tier || '',
        r.status,
        r.payment_status,
        (regAmountCents / 100).toFixed(2),
        (extrasTotal / 100).toFixed(2),
        (ticketsTotal / 100).toFixed(2),
        scheduleRequestDetails,
        (scheduleRequestFee / 100).toFixed(2),
        (grandTotal / 100).toFixed(2),
        formatDateTime(r.created_at)
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event?.name || 'event'}-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Registrations exported successfully');
  };

  const syncAllToSheets = async () => {
    if (!event?.google_sheets_webhook_url) {
      toast.error('No Google Sheets configured for this event');
      return;
    }
    
    if (registrations.length === 0) {
      toast.error('No registrations to sync');
      return;
    }

    setSyncing(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Sync ALL registrations (force resync)
      for (const reg of registrations) {
        const registrantData = reg.registrant_data || {};
        const customResponses = (registrantData as any).custom_responses || {};
        const pricingBreakdown = reg.pricing_breakdown || {};
        
        const coachName = (registrantData as any).coach?.name || (registrantData as any).athlete?.name || registrantData.contact_name || '';
        const nameParts = coachName.split(' ');
        const coachFirst = nameParts[0] || '';
        const coachLast = nameParts.slice(1).join(' ') || '';

        // Extract special request from form_X_special_request_details keys
        let specialRequest = '';
        let rateYourTeam = '';
        Object.entries(customResponses).forEach(([key, value]) => {
          if (key.includes('special_request_details') && value) {
            specialRequest = String(value);
          }
          // Rate your team is stored with a specific form field ID (1763417540688)
          if (key.includes('1763417540688') && value) {
            rateYourTeam = String(value);
          }
        });

        // Extract extras string from pricing_breakdown for display
        const extrasArray = (pricingBreakdown as any).extras || [];
        const extrasStr = extrasArray.map((e: any) => e.name || '').filter(Boolean).join(', ');

        // Read directly from dedicated columns (already in dollars)
        const basePrice = reg.registration_amount ?? 0;
        const extrasTotal = reg.extras_amount ?? 0;
        const ticketsTotal = reg.tickets_amount ?? 0;
        const specialRequestFee = reg.special_request_amount ?? 0;

        console.log('📊 Financial amounts for registration:', reg.id, {
          registration_amount: reg.registration_amount,
          extras_amount: reg.extras_amount,
          tickets_amount: reg.tickets_amount,
          special_request_amount: reg.special_request_amount
        });

        const sheetData = {
          team_name: (registrantData as any).team?.name || '',
          division: (registrantData as any).team?.division_id || registrantData.division || '',
          coach_first: coachFirst,
          coach_last: coachLast,
          email: (registrantData as any).coach?.email || registrantData.contact_email || '',
          cell: (registrantData as any).coach?.phone || registrantData.contact_phone || '',
          registered: 'Yes',
          paid: reg.status === 'confirmed' ? 'Paid' : (reg.status as string) === 'waitlisted' ? 'Waitlisted' : 'Pending',
          pay_type: reg.payment_method || '',
          total_amount: (reg.pricing_breakdown?.total || 0) / 100,
          amount_paid: reg.payment_status === 'paid' ? (reg.pricing_breakdown?.total || 0) / 100 : 0,
          owe_still: reg.payment_status === 'paid' ? 0 : (reg.pricing_breakdown?.total || 0) / 100,
          // Category breakdowns
          registration_amount: basePrice,
          extras_amount: extrasTotal,
          special_request_amount: specialRequestFee,
          tickets_amount: ticketsTotal,
          schedule_request: specialRequest,
          rate_your_team: rateYourTeam,
          extras: extrasStr,
          registered_at: formatDateTime(reg.created_at),
        };

        // Use the event's configured column mapping if available
        const columnMappingToUse = event.google_sheets_column_mapping || null;
        const summaryMapping = (event as any).google_sheets_summary_mapping || null;
        const divisionTotals = (event as any).google_sheets_division_totals || null;

        const { data: result, error } = await invokeEdgeFunction('google-sheets-sync', {
          body: {
            spreadsheetId: event.google_sheets_webhook_url,
            sheetName: event.google_sheets_tab_name || 'Sheet1',
            action: 'append',
            data: sheetData,
            columnMapping: columnMappingToUse,
            summaryMapping,
            divisionTotals,
            eventId: event.id
          }
        });

        if (error || !result?.success) {
          errorCount++;
          console.error('Sync error for registration', reg.id, error || result?.error);
        } else {
          successCount++;
          // Mark registration as synced
          await supabase
            .from('registrations')
            .update({ synced_to_sheets: true })
            .eq('id', reg.id);
        }
      }

      if (errorCount === 0) {
        toast.success(`Synced ${successCount} registrations to Google Sheets`);
      } else {
        toast.warning(`Synced ${successCount} registrations, ${errorCount} failed`);
      }
    } catch (error) {
      console.error('Sync all error:', error);
      toast.error('Failed to sync registrations');
    } finally {
      setSyncing(false);
    }
  };

  const handleResendEmail = (registrationId: string) => {
    // TODO: Implement resend confirmation email
    toast.success('Confirmation email resent');
  };

  const handleCancel = (registrationId: string) => {
    // TODO: Implement cancellation with confirmation dialog
    toast.success('Registration cancelled');
  };

  const handleRefund = (registrationId: string) => {
    // TODO: Implement refund with Stripe
    toast.success('Refund initiated');
  };

  const handleMarkPaid = async (registrationId: string) => {
    try {
      // Update the database
      const { error } = await supabase
        .from('registrations')
        .update({ 
          payment_status: 'paid',
          status: 'confirmed'
        })
        .eq('id', registrationId);

      if (error) throw error;

      // Find the registration for sync
      const registration = registrations.find(r => r.id === registrationId);
      
      // Sync to Google Sheets if configured
      if (event?.google_sheets_webhook_url && registration) {
        const registrantData = registration.registrant_data || {};
        const pricing = registration.pricing_breakdown || {};
        
        const coachName = (registrantData as any).coach?.name || (registrantData as any).athlete?.name || registrantData.contact_name || '';
        const nameParts = coachName.split(' ');
        const coachFirst = nameParts[0] || '';
        const coachLast = nameParts.slice(1).join(' ') || '';

        const sheetData = {
          team_name: (registrantData as any).team?.name || '',
          division: (registrantData as any).team?.division_id || (pricing as any).division || '',
          coach_name: coachName,
          coach_first: coachFirst,
          coach_last: coachLast,
          contact_email: (registrantData as any).coach?.email || registrantData.contact_email || '',
          contact_phone: (registrantData as any).coach?.phone || registrantData.contact_phone || '',
          payment_status: 'paid',
          total_amount: (((pricing as any).total || 0) / 100).toFixed(2),
          amount_paid: (((pricing as any).total || 0) / 100).toFixed(2),
        };

        await invokeEdgeFunction('google-sheets-sync', {
          body: {
            spreadsheetId: event.google_sheets_webhook_url,
            sheetName: event.google_sheets_tab_name || 'Sheet1',
            action: 'append',
            data: [sheetData],
            columnMapping: event.google_sheets_column_mapping || null,
          }
        });
      }

      // Send admin notification
      if (registration) {
        const registrantData = registration.registrant_data || {};
        const pricing = registration.pricing_breakdown || {};
        const coachName = (registrantData as any).coach?.name || registrantData.contact_name || 'Unknown';
        const teamName = (registrantData as any).team?.name || '';
        const amount = (((pricing as any).total || 0) / 100).toFixed(2);

        await invokeEdgeFunction('communication-handler', {
          body: {
            to: ['nancy@zw2.com', 'mike@recruitlook.com'],
            subject: `💰 Payment Received: ${event?.name || 'Event'}`,
            html: `
              <h2>Payment Marked as Received</h2>
              <p>A registration payment has been manually marked as paid:</p>
              <ul>
                <li><strong>Event:</strong> ${event?.name || 'Unknown'}</li>
                <li><strong>Team:</strong> ${teamName || 'N/A'}</li>
                <li><strong>Coach:</strong> ${coachName}</li>
                <li><strong>Amount:</strong> $${amount}</li>
              </ul>
            `,
            metadata: {
              type: 'admin_notification',
              registration_id: registrationId,
            }
          }
        });
      }

      toast.success('Registration marked as paid and synced');
      // Refresh the data
      window.location.reload();
    } catch (error: any) {
      console.error('Mark paid error:', error);
      toast.error(error.message || 'Failed to mark as paid');
    }
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin-dashboard">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {event?.name || 'Event Registrations'}
            </h1>
            <p className="text-muted-foreground">
              {stats.total} total registrations • {formatCurrency(stats.revenue)} revenue
            </p>
          </div>

          <div className="flex gap-2">
            {event?.google_sheets_webhook_url && (
              <Button onClick={syncAllToSheets} variant="outline" disabled={syncing}>
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sheet className="h-4 w-4 mr-2" />
                )}
                Sync All to Sheets
              </Button>
            )}
            <Button onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Confirmed</p>
          <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Revenue</p>
          <p className="text-2xl font-bold">{formatCurrency(stats.revenue)}</p>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Collected</p>
          <p className="text-xl font-semibold text-green-600">{formatCurrency(stats.collectedRevenue)}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Pending (Pay Later)</p>
          <p className="text-xl font-semibold text-yellow-600">{formatCurrency(stats.pendingRevenue)}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Total Discounts</p>
          <p className="text-xl font-semibold text-blue-600">{formatCurrency(stats.discounts)}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Comped</p>
          <p className="text-xl font-semibold text-muted-foreground">{stats.comped}</p>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm font-medium text-muted-foreground mb-2">Registrations</p>
          <div className="flex justify-between text-sm">
            <span className="text-green-600">Paid: {formatCurrency(stats.registrationsCollected)}</span>
            <span className="text-yellow-600">Pending: {formatCurrency(stats.registrationsPending)}</span>
          </div>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm font-medium text-muted-foreground mb-2">Extras</p>
          <div className="flex justify-between text-sm">
            <span className="text-green-600">Paid: {formatCurrency(stats.extrasCollected)}</span>
            <span className="text-yellow-600">Pending: {formatCurrency(stats.extrasPending)}</span>
          </div>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm font-medium text-muted-foreground mb-2">Special Requests</p>
          <div className="flex justify-between text-sm">
            <span className="text-green-600">Paid: {formatCurrency(stats.specialRequestsCollected)}</span>
            <span className="text-yellow-600">Pending: {formatCurrency(stats.specialRequestsPending)}</span>
          </div>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <p className="text-sm font-medium text-muted-foreground mb-2">Tickets</p>
          <div className="flex justify-between text-sm">
            <span className="text-green-600">Paid: {formatCurrency(stats.ticketsCollected)}</span>
            <span className="text-yellow-600">Pending: {formatCurrency(stats.ticketsPending)}</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="mb-6">
        <SignedWaiversPanel eventId={eventId} />
      </div>

      <div className="mb-6">
        <TeamDataGrids eventId={eventId!} />
      </div>


      {/* Status Tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">
            All ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="confirmed">
            Confirmed ({stats.confirmed})
          </TabsTrigger>
          <TabsTrigger value="pending">
            Pending ({stats.pending})
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            Cancelled ({stats.cancelled})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Registration List */}
      {filteredRegistrations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No registrations found
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredRegistrations.map((registration) => (
            <RegistrationCard
              key={registration.id}
              registration={registration}
              displayTotalCents={normalizedById[registration.id]?.totalCents ?? (registration.pricing_breakdown?.total || 0)}
              onViewDetails={() => setSelectedRegistration(registration)}
              onResendEmail={() => handleResendEmail(registration.id)}
              onCancel={() => handleCancel(registration.id)}
              onRefund={() => handleRefund(registration.id)}
              onMarkPaid={() => handleMarkPaid(registration.id)}
            />
          ))}
        </div>
      )}
      
      {/* Registration Details Modal */}
      <RegistrationDetailsModal
        registration={selectedRegistration}
        open={!!selectedRegistration}
        onClose={() => setSelectedRegistration(null)}
        onResendEmail={() => {
          if (selectedRegistration) {
            handleResendEmail(selectedRegistration.id);
          }
        }}
        onCancel={() => {
          if (selectedRegistration) {
            handleCancel(selectedRegistration.id);
            setSelectedRegistration(null);
          }
        }}
        onRefund={() => {
          if (selectedRegistration) {
            handleRefund(selectedRegistration.id);
          }
        }}
      />
    </div>
  );
}
